import React from "react";
import {
  Sun,
  Moon,
  Type,
  Contrast,
  Palette,
  ChevronDown,
  Settings
} from "lucide-react";
import { useTranslation } from "react-i18next";

import { useAccessibility } from "../contexts/AccessibilityContext";
import { useThemeMode } from "../contexts/ThemeContext";

export default function ProfileAccessibility() {
  const { settings, updateSetting } = useAccessibility();
  const { mode, toggleTheme } = useThemeMode();
  const { t } = useTranslation();

  const handleToggleDarkMode = () => toggleTheme();
  const handleToggleContrast = () => {
    updateSetting("contrast", settings.contrast === "high-contrast" ? "normal" : "high-contrast");
  };

  const darkMode = mode === "dark";
  const highContrast = settings.contrast === "high-contrast";

  // Map discrete font sizes to numeric values for the slider display (approximate)
  const getFontSizeValue = (size) => {
    if (size === "extra-large") return 24;
    if (size === "large") return 18;
    return 14;
  };

  const handleFontSizeChange = (val) => {
    if (val >= 21) updateSetting("fontSize", "extra-large");
    else if (val >= 16) updateSetting("fontSize", "large");
    else updateSetting("fontSize", "normal");
  };

  const largeText = getFontSizeValue(settings.fontSize);

  return (
    <div className="w-full space-y-12 animate-in fade-in slide-in-from-bottom-4 duration-500">
      {/* Compact Header */}
      <div className="flex items-center gap-3 sm:gap-4">
        <div className="w-10 h-10 sm:w-12 sm:h-12 bg-[#0f7699]/10 dark:bg-[#4fc3f7]/10 rounded-xl sm:rounded-2xl flex items-center justify-center text-[#0f7699] dark:text-[#4fc3f7]">
          <Settings size={20} className="sm:hidden" /><Settings size={24} className="hidden sm:block" />
        </div>
        <div>
          <h1 className="text-lg sm:text-2xl font-black text-[#0f7699] dark:text-[#4fc3f7] tracking-tight">
            {t("profile.Barrierefreiheit", "Barrierefreiheit")}
          </h1>
          <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 font-medium mt-0.5 hidden sm:block">
            {t("profile.Passe die Anzeige und Lesbarkeit deiner Seite an", "Passe die Anzeige und Lesbarkeit deiner Seite an.")}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 md:gap-8">
        {/* Dunkelmodus Toggle Card */}
        <div className="group bg-white dark:bg-slate-800 rounded-2xl sm:rounded-[2rem] md:rounded-[2.5rem] border-2 border-slate-100 dark:border-slate-700 p-5 sm:p-6 md:p-8 shadow-sm hover:shadow-xl hover:border-[#0f7699]/20 dark:hover:border-[#4fc3f7]/20 transition-all duration-300">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-5">
              <div className={`w-14 h-14 rounded-2xl flex items-center justify-center transition-all duration-500 ${darkMode ? "bg-slate-900 text-yellow-400 shadow-lg shadow-black/20" : "bg-blue-50 text-[#0f7699]"}`}>
                {darkMode ? <Moon size={24} /> : <Sun size={24} />}
              </div>
              <div className="space-y-0.5">
                <h3 className="text-lg font-black text-slate-800 dark:text-white tracking-tight">
                  {t("profile.Dunkelmodus", "Dunkelmodus")}
                </h3>
                <p className="text-xs text-slate-400 dark:text-slate-500 font-medium">
                  {darkMode ? t("profile.Aktiviert", "Aktiviert") : t("profile.Deaktiviert", "Deaktiviert")}
                </p>
              </div>
            </div>

            <button
              onClick={handleToggleDarkMode}
              className={`
                relative w-16 h-8 rounded-full transition-all duration-500 flex items-center px-1
                ${darkMode ? "bg-slate-900 shadow-inner" : "bg-slate-100 shadow-inner"}
              `}
            >
              <div className={`
                w-6 h-6 rounded-full bg-white shadow-md transition-all duration-500 transform
                ${darkMode ? "translate-x-8" : "translate-x-0"}
              `}></div>
            </button>
          </div>
        </div>

        {/* High Contrast Toggle Card */}
        <div className="group bg-white dark:bg-slate-800 rounded-2xl sm:rounded-[2rem] md:rounded-[2.5rem] border-2 border-slate-100 dark:border-slate-700 p-5 sm:p-6 md:p-8 shadow-sm hover:shadow-xl hover:border-[#0f7699]/20 dark:hover:border-[#4fc3f7]/20 transition-all duration-300">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-5">
              <div className={`w-14 h-14 rounded-2xl flex items-center justify-center transition-all duration-500 ${highContrast ? "bg-black text-white shadow-lg shadow-black/20" : "bg-orange-50 text-orange-600"}`}>
                <Contrast size={24} />
              </div>
              <div className="space-y-0.5">
                <h3 className="text-lg font-black text-slate-800 dark:text-white tracking-tight">
                  {t("profile.Hoher Kontrast", "Hoher Kontrast")}
                </h3>
                <p className="text-xs text-slate-400 dark:text-slate-500 font-medium">
                  {highContrast ? t("profile.Aktiviert", "Aktiviert") : t("profile.Deaktiviert", "Deaktiviert")}
                </p>
              </div>
            </div>

            <button
              onClick={handleToggleContrast}
              className={`
                relative w-16 h-8 rounded-full transition-all duration-500 flex items-center px-1
                ${highContrast ? "bg-black shadow-inner" : "bg-slate-100 shadow-inner"}
              `}
            >
              <div className={`
                w-6 h-6 rounded-full bg-white shadow-md transition-all duration-500 transform
                ${highContrast ? "translate-x-8" : "translate-x-0"}
              `}></div>
            </button>
          </div>
        </div>

        {/* Font Size Slider Card */}
        <div className="lg:col-span-2 group bg-white dark:bg-slate-800 rounded-2xl sm:rounded-[2rem] md:rounded-[3rem] border-2 border-slate-100 dark:border-slate-700 p-5 sm:p-7 md:p-10 shadow-sm hover:shadow-2xl hover:border-[#0f7699]/20 dark:hover:border-[#4fc3f7]/20 transition-all duration-500">
          <div className="flex flex-col md:flex-row gap-10 items-center">
            <div className="shrink-0 flex items-center gap-6">
              <div className="w-16 h-16 bg-blue-50 dark:bg-[#4fc3f7]/10 text-[#0f7699] dark:text-[#4fc3f7] rounded-[1.25rem] flex items-center justify-center shadow-lg shadow-blue-900/5">
                <Type size={28} />
              </div>
              <div className="space-y-1">
                <h3 className="text-xl font-black text-slate-900 dark:text-white tracking-tight">
                  {t("profile.Schriftgröße", "Schriftgröße")}
                </h3>
                <div className="px-3 py-1 bg-slate-900 text-white rounded-lg inline-block text-[10px] font-black uppercase tracking-widest shadow-lg shadow-black/10">
                  {largeText}px
                </div>
              </div>
            </div>

            <div className="flex-1 w-full space-y-4">
              <input
                type="range"
                min="12"
                max="24"
                step="1"
                value={largeText}
                onChange={(e) => handleFontSizeChange(parseInt(e.target.value))}
                className="w-full h-2 bg-slate-100 rounded-lg appearance-none cursor-pointer accent-[#0f7699] hover:accent-[#0d6482] transition-all"
              />
              <div className="flex justify-between px-1">
                <span className="text-[10px] font-black text-slate-300 uppercase tracking-widest">Small</span>
                <span className="text-[10px] font-black text-slate-300 uppercase tracking-widest text-center">Normal</span>
                <span className="text-[10px] font-black text-slate-300 uppercase tracking-widest text-right">Large</span>
              </div>
            </div>
          </div>

          <div className="mt-10 p-8 rounded-[2rem] bg-slate-50 dark:bg-slate-700 border border-slate-100 dark:border-slate-600 overflow-hidden relative group/preview transition-all duration-500 hover:bg-white dark:hover:bg-slate-600 hover:border-[#0f7699]/20 dark:hover:border-[#4fc3f7]/20">
            <div className="absolute top-4 right-6 px-3 py-1 bg-white dark:bg-slate-600 border border-slate-200 dark:border-slate-500 rounded-lg text-[10px] font-black text-slate-400 dark:text-slate-300 uppercase tracking-widest group-hover/preview:border-[#0f7699]/20 dark:group-hover/preview:border-[#4fc3f7]/20 transition-all">
              Preview
            </div>
            <p
              className="text-slate-700 dark:text-slate-200 leading-relaxed font-medium transition-all duration-300"
              style={{ fontSize: `${largeText}px` }}
            >
              {t("profile.Dies ist ein Beispieltext, um die Lesbarkeit der gewählten Schriftgröße zu testen.", "Dies ist ein Beispieltext, um die Lesbarkeit der gewählten Schriftgröße zu testen. Die Lesbarkeit ist ein wichtiger Aspekt moderner Benutzeroberflächen.")}
            </p>
          </div>
        </div>

        {/* Color Scheme Select Card */}
        <div className="lg:col-span-2 group bg-white dark:bg-slate-800 rounded-2xl sm:rounded-[2rem] md:rounded-[2.5rem] border-2 border-slate-100 dark:border-slate-700 p-5 sm:p-6 md:p-8 shadow-sm hover:shadow-xl hover:border-[#0f7699]/20 dark:hover:border-[#4fc3f7]/20 transition-all duration-300">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-5">
              <div className="w-14 h-14 bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 rounded-2xl flex items-center justify-center shadow-lg shadow-purple-900/5">
                <Palette size={24} />
              </div>
              <div className="space-y-0.5">
                <h3 className="text-lg font-black text-slate-800 dark:text-white tracking-tight">
                  {t("profile.Design-Thema", "Design-Thema")}
                </h3>
                <p className="text-xs text-slate-400 dark:text-slate-500 font-medium">
                  {t("profile.Wähle dein bevorzugtes Farbschema", "Wähle dein bevorzugtes Farbschema")}
                </p>
              </div>
            </div>

            <div className="relative w-full sm:w-64 group/select">
              <select
                value={darkMode ? "dark" : "light"}
                onChange={(e) => {
                  if (e.target.value === "dark" && mode !== "dark") toggleTheme();
                  if (e.target.value === "light" && mode !== "light") toggleTheme();
                }}
                className="w-full h-14 pl-6 pr-12 bg-slate-50 dark:bg-slate-700 border-2 border-transparent focus:border-[#0f7699] dark:focus:border-[#4fc3f7] focus:bg-white dark:focus:bg-slate-600 rounded-2xl text-slate-700 dark:text-white font-bold appearance-none outline-none transition-all cursor-pointer"
              >
                <option value="light">{t("profile.Hell", "Heller Modus")}</option>
                <option value="dark">{t("profile.Dunkel", "Dunkler Modus")}</option>
              </select>
              <div className="absolute top-1/2 right-5 -translate-y-1/2 text-slate-400 pointer-events-none group-focus-within/select:text-[#0f7699] transition-colors">
                <ChevronDown size={20} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
